import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Play, Pause, Volume2, VolumeX } from "lucide-react";

const HERO_VIDEO_1 = "https://videos.pexels.com/video-files/7578540/7578540-hd_1280_720_30fps.mp4";
const HERO_VIDEO_2 = "https://videos.pexels.com/video-files/5586205/5586205-hd_1920_1080_30fps.mp4";
const HERO_POSTER = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2000&q=85";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.5 + i * 0.12, duration: 1.0, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: 1.4 + i * 0.08, duration: 0.8 } }),
};

export default function HeroV2() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    tryPlay();
    v.addEventListener("canplay", tryPlay, { once: true });
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = videoRef.current; if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
  };

  return (
    <section
      ref={ref}
      data-testid="hero-v2"
      className="relative min-h-[100svh] bg-charcoal text-charcoal_ink overflow-hidden"
    >
      {/* Parallax background video with cinematic vignette */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay loop muted playsInline preload="auto" poster={HERO_POSTER}
        >
          <source src={HERO_VIDEO_1} type="video/mp4" />
          <source src={HERO_VIDEO_2} type="video/mp4" />
        </video>
        {/* Heavy cinematic tint */}
        <div className="absolute inset-0 bg-charcoal/70" />
        <div className="absolute inset-0 v2-cine-vignette" />
        <div className="absolute inset-0 v2-cine-fade-bottom" />
        <div className="absolute inset-0 v2-cine-fade-left" />
      </motion.div>

      {/* Grid overlay */}
      <div className="absolute inset-0 v2-gridlines opacity-40 pointer-events-none" />

      {/* Top corner meta */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
        className="absolute top-[130px] left-6 md:left-10 z-10 flex items-center gap-3 chapter-num text-charcoal_ink/70"
      >
        <span className="w-8 h-px bg-glow_lime glow-box" />
        <span>001 / KLIVING · MMXXV — DEMO 02</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
        className="absolute top-[130px] right-6 md:right-10 z-10 flex items-center gap-2 chapter-num text-charcoal_ink/70"
      >
        <MapPin className="w-3 h-3 text-glow_lime glow-icon" /> 20.29°N · 85.82°E — BHUBANESWAR
      </motion.div>

      {/* Main headline */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 min-h-[100svh] flex flex-col justify-center px-6 md:px-10 max-w-[1400px] mx-auto"
      >
        <div className="max-w-[1100px]">
          <motion.div
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="chapter-num mb-6 md:mb-8 flex items-center gap-3"
          >
            <span className="w-2 h-2 rounded-full bg-glow_lime glow-box" />
            <span className="glow-lime">— LANDMARK DEVELOPMENTS · SINCE 2019</span>
          </motion.div>

          <h1 className="font-serif-display text-charcoal_ink leading-[0.92] tracking-[-0.02em] text-[52px] sm:text-[72px] md:text-[96px] lg:text-[128px]">
            <span className="mask-line"><motion.span custom={0} variants={lineVariants} initial="hidden" animate="show" className="italic font-light">Crafting</motion.span></span>
            <span className="mask-line"><motion.span custom={1} variants={lineVariants} initial="hidden" animate="show">Landmark</motion.span></span>
            <span className="mask-line"><motion.span custom={2} variants={lineVariants} initial="hidden" animate="show">
              Developments<span className="glow-lime">.</span>
            </motion.span></span>
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <motion.p
              variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="md:col-span-7 text-charcoal_ink/75 text-base md:text-lg leading-relaxed max-w-lg"
            >
              A boutique developer building residential and commercial landmarks in Bhubaneswar — where materials are chosen slowly, drawings are drawn twice, and every plot is treated as public architecture.
            </motion.p>

            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1} className="md:col-span-5 flex flex-col items-start md:items-end gap-3">
              <a href="#projects-v2" data-testid="hero-v2-cta-portfolio" className="btn-kl-v2 btn-kl-v2-solid w-full sm:w-auto justify-center md:justify-start">
                View Portfolio <span className="w-1.5 h-1.5 rounded-full bg-current" />
              </a>
              <a href="#contact-v2" data-testid="hero-v2-cta-visit" className="btn-kl-v2 w-full sm:w-auto justify-center md:justify-start">
                Schedule a Visit <span className="w-1.5 h-1.5 rounded-full bg-current" />
              </a>
            </motion.div>
          </div>
        </div>

        {/* Bottom bar */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0, duration: 0.8 }}
          className="absolute bottom-8 left-6 md:left-10 flex items-center gap-5 text-charcoal_ink/80"
        >
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={togglePlay}
              className="w-10 h-10 grid place-items-center border border-charcoal_ink/30 text-charcoal_ink hover:bg-glow_lime hover:border-glow_lime hover:text-charcoal transition-colors duration-300"
              aria-label={playing ? "Pause video" : "Play video"}
            >
              {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={toggleMute}
              className="w-10 h-10 grid place-items-center border border-charcoal_ink/30 text-charcoal_ink hover:bg-glow_lime hover:border-glow_lime hover:text-charcoal transition-colors duration-300"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <ArrowDown className="w-4 h-4 animate-bounce text-glow_lime glow-icon" />
            <span className="chapter-num">SCROLL</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.0, duration: 0.8 }}
          className="absolute bottom-8 right-6 md:right-10 grid grid-cols-3 gap-6 text-charcoal_ink/70 text-right"
        >
          <div>
            <div className="font-serif-display text-3xl md:text-4xl text-charcoal_ink">14</div>
            <div className="chapter-num mt-1 text-glow_lime">PROJECTS</div>
          </div>
          <div>
            <div className="font-serif-display text-3xl md:text-4xl text-charcoal_ink">2.8M</div>
            <div className="chapter-num mt-1 text-glow_lime">SQ. FT.</div>
          </div>
          <div>
            <div className="font-serif-display text-3xl md:text-4xl text-charcoal_ink">6</div>
            <div className="chapter-num mt-1 text-glow_lime">YEARS</div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
